import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platform } from '../../api/client.js';

/**
 * Upload a customer's light and dark logos.
 *
 * These used to be blob names typed into a text box, pointing at images
 * somebody had put in a storage account by some other route. Nobody could see
 * from this screen whether the file existed, what it looked like, or how big
 * it was — and the portal never once served one successfully.
 *
 * So the file arrives here, and what is shown is the image itself.
 *
 * Vizora staff only. This whole console 404s for anyone outside the platform
 * admin group, so a customer cannot put an image into their own portal chrome.
 */
export default function LogoUpload({ customerId }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'logos', customerId],
    queryFn: () => platform.listLogos(customerId),
    enabled: !!customerId
  });

  if (!customerId) {
    return (
      <p className="text-secondary small mb-0">
        Save the customer first, then upload its logos.
      </p>
    );
  }

  if (isLoading) return <div className="spinner-border spinner-border-sm" />;

  const byVariant = Object.fromEntries((data?.logos ?? []).map(l => [l.variant, l]));

  return (
    <div className="row g-3">
      <Variant variant="light" label="Light background"
               help="Shown on the light theme, which is the default."
               current={byVariant.light} customerId={customerId}
               queryClient={queryClient} />
      <Variant variant="dark" label="Dark background"
               help="Shown on the dark theme. Leave empty to reuse the light one."
               current={byVariant.dark} customerId={customerId}
               queryClient={queryClient} />
    </div>
  );
}

function Variant({ variant, label, help, current, customerId, queryClient }) {
  const inputRef = useRef(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  /* Shown from the chosen file rather than from the server, so the person sees
     what they picked before it is committed - and, if it is wrong, sees that
     it is wrong before a customer does. */
  const [preview, setPreview] = useState(null);

  const upload = useMutation({
    mutationFn: (body) => platform.uploadLogo(customerId, body),
    onSuccess: (result) => {
      setError(null);
      setNote(result?.note ?? 'Saved.');
      queryClient.invalidateQueries({ queryKey: ['platform', 'logos', customerId] });
    },
    onError: (err) => {
      setPreview(null);
      // The server's own words: it validated the bytes and knows why.
      setError(err.body?.error ?? err.message);
    }
  });

  const remove = useMutation({
    mutationFn: () => platform.deleteLogo(customerId, variant),
    onSuccess: () => {
      setPreview(null); setError(null); setNote('Removed.');
      queryClient.invalidateQueries({ queryKey: ['platform', 'logos', customerId] });
    }
  });

  const choose = (file) => {
    if (!file) return;
    setError(null); setNote(null);

    const reader = new FileReader();
    reader.onload = () => {
      /* readAsDataURL gives "data:<type>;base64,<payload>". Only the payload
         is sent: the declared type is discarded because the server derives it
         from the bytes, and sending it would imply it were trusted. */
      const dataUrl = String(reader.result ?? '');
      const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
      if (!base64) { setError('That file could not be read.'); return; }

      setPreview(dataUrl);
      upload.mutate({ variant, fileName: file.name, dataBase64: base64 });
    };
    reader.onerror = () => setError('That file could not be read.');
    reader.readAsDataURL(file);
  };

  const busy = upload.isPending || remove.isPending;
  const shown = preview ?? null;

  return (
    <div className="col-md-6">
      <div className="logo-upload">
        <div className="fw-medium">{label}</div>
        <div className="form-text mb-2">{help}</div>

        <div className={`logo-preview ${variant === 'dark' ? 'is-dark' : ''}`}>
          {shown
            ? <img src={shown} alt="" />
            : current
              ? <span className="logo-preview-note">
                  {current.fileName || current.contentType}
                </span>
              : <span className="logo-preview-note">No logo</span>}
        </div>

        {current && (
          <div className="admin-muted small mt-1">
            {Math.round(current.byteLength / 1024)} KB · {current.contentType}
          </div>
        )}

        <div className="d-flex gap-2 mt-2">
          <input ref={inputRef} type="file" className="d-none"
                 accept="image/png,image/jpeg,image/webp,image/gif"
                 onChange={e => { choose(e.target.files?.[0]); e.target.value = ''; }} />
          <button type="button" className="btn btn-sm btn-outline-secondary"
                  disabled={busy} onClick={() => inputRef.current?.click()}>
            {busy ? 'Working…' : current ? 'Replace' : 'Upload'}
          </button>
          {current && (
            <button type="button" className="btn btn-sm btn-outline-danger"
                    disabled={busy} onClick={() => remove.mutate()}>
              Remove
            </button>
          )}
        </div>

        {/* The accept attribute above is a convenience for the file picker and
            nothing more - it is trivially bypassed, and the server refuses on
            the decoded bytes regardless. SVG is deliberately absent from both. */}
        <div className="form-text mt-1">
          PNG, JPEG, WebP or GIF, up to 512&nbsp;KB. Not SVG.
        </div>

        {error && <p className="text-danger small mt-2 mb-0">{error}</p>}
        {note && !error && <p className="text-secondary small mt-2 mb-0">{note}</p>}
      </div>
    </div>
  );
}
